import React, { useState, useEffect } from "react";
import "./AdvancedChemistryPractice.css";
import questionPool from "./QuestionPool.json";
import atomicData from "./AtomicDataPool.json";
import BohrModelVisualizer from "./BohrModelVisualizer";
import ReactMarkdown from "react-markdown";
import PeriodicTable from "./PeriodicTable";
import CompoundPractice from "./CompoundPractice";

// --- INTERACTIVE BUILDER ---
function BohrModelBuilder({ element, onSubmit, feedback, isCorrect, showSolution, solution, explanation }) {
    const [userShell, setUserShell] = useState(() =>
        element ? Array(element.shellConfig.length).fill(0) : []
    );
    useEffect(() => {
        if (element) setUserShell(Array(element.shellConfig.length).fill(0));
    }, [element]);
    function handleAddElectron(shellIdx) {
        setUserShell(shells =>
            shells.map((count, idx) =>
                idx === shellIdx
                    ? Math.min(count + 1, element.shellConfig[shellIdx])
                    : count
            )
        );
    }
    function handleRemoveElectron(shellIdx) {
        setUserShell(shells =>
            shells.map((count, idx) => (idx === shellIdx ? Math.max(count - 1, 0) : count))
        );
    }
    function handleSubmit() { onSubmit(userShell); }

    return (
        <div>
            <BohrModelVisualizer element={element} userShell={userShell} />
            <div className="bohr-builder-controls">
                {userShell.map((count, idx) => (
                    <div key={idx} className="shell-control">
                        <span>Shell {idx + 1}:</span>
                        <button onClick={() => handleRemoveElectron(idx)} disabled={count === 0}>–</button>
                        <span className="electron-count">{count}</span>
                        <button
                            onClick={() => handleAddElectron(idx)}
                            disabled={count >= element.shellConfig[idx]}
                        >+</button>
                        <span className="max-hint">/ {element.shellConfig[idx]} max</span>
                    </div>
                ))}
            </div>
            <button className="submit-btn" onClick={handleSubmit} style={{ marginTop: "1.1em" }}>Submit</button>
            {feedback && (
                <div className={`feedback ${isCorrect === false ? "incorrect" : isCorrect ? "correct" : ""}`}>
                    {feedback}
                </div>
            )}
            {showSolution && (
                <div className="solution-card">
                    <b>Correct Configuration:</b> <span className="solution-answer">{element.shellConfig.join(", ")}</span>
                    <br />
                    {explanation && (
                        <span className="explanation">{explanation}</span>
                    )}
                    <button onClick={solution.handleNext} className="next-btn">Next Challenge</button>
                </div>
            )}
            {isCorrect && (
                <button onClick={solution.handleNext} className="next-btn">Next Challenge</button>
            )}
        </div>
    );
}

export default function AdvancedChemistryPractice() {
    const questions = questionPool;
    const elements = atomicData;
    const [mode, setMode] = useState("atom"); // "atom" or "compound"

    const [step, setStep] = useState("menu");
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [selectedQuestionId, setSelectedQuestionId] = useState("");
    const [userAnswer, setUserAnswer] = useState("");
    const [feedback, setFeedback] = useState("");
    const [instructions, setInstructions] = useState("");
    const [showSolution, setShowSolution] = useState(false);
    const [answered, setAnswered] = useState([]);
    const [isCorrect, setIsCorrect] = useState(null);

    // Periodic Table selection (right panel)
    const [selectedElementSymbol, setSelectedElementSymbol] = useState(null);

    useEffect(() => {
        fetch("/AdvancedChemistryPractice/Instructions.md")
            .then(res => res.text())
            .then(setInstructions);
    }, []);

    const currentIndex = selectedQuestion
        ? questions.findIndex(q => q.id === selectedQuestion.id)
        : -1;

    function handleDropdownChange(e) {
        const qId = e.target.value;
        setSelectedQuestionId(qId);
        const q = questions.find(q => q.id === qId);
        if (q) {
            setTimeout(() => handleSelectQuestion(q), 200);
        }
    }

    function handleSelectQuestion(q) {
        setSelectedQuestion(q);
        setStep("practice");
        setUserAnswer("");
        setFeedback("");
        setShowSolution(false);
        setIsCorrect(null);
        setSelectedQuestionId(q.id);
        // Optionally: also select element in periodic table
        setSelectedElementSymbol(q.targetElement);
    }

    // (Optional) When periodic table tile is clicked, show its Bohr model
    function handleElementSelect(symbol) {
        setSelectedElementSymbol(symbol);
        // Optionally auto-launch a "build" challenge for this element:
        // const buildQ = questions.find(q => q.type === "build" && q.targetElement === symbol);
        // if (buildQ) handleSelectQuestion(buildQ);
    }

    // For non-build questions
    function handleSubmit() {
        if (!userAnswer || userAnswer.length === 0) {
            setFeedback("Please enter your answer.");
            setShowSolution(false);
            return;
        }
        let correct = false;
        const element = elements.find(e => e.symbol === selectedQuestion.targetElement);
        if (selectedQuestion.type === "identify") {
            const answer = (selectedQuestion.answer || "").toLowerCase();
            const name = element?.name?.toLowerCase();
            correct =
                userAnswer.trim().toLowerCase() === answer ||
                userAnswer.trim().toLowerCase() === (name || "");
        } else if (selectedQuestion.type === "shell_count") {
            correct = userAnswer.trim() === selectedQuestion.answer;
        } else if (selectedQuestion.type === "isotope") {
            correct = userAnswer.trim().toLowerCase() === (selectedQuestion.answer || "").toLowerCase();
        } else if (selectedQuestion.type === "ion") {
            correct = userAnswer.trim() === selectedQuestion.answer;
        }

        setIsCorrect(correct);
        if (correct) {
            setFeedback("✅ Correct!");
            setShowSolution(false);
        } else {
            setFeedback("❌ Incorrect. See the correct answer below.");
            setShowSolution(true);
        }
        if (selectedQuestion && !answered.includes(selectedQuestion.id)) {
            setAnswered([...answered, selectedQuestion.id]);
        }
    }

    function handleBuildSubmit(userShell) {
        const element = elements.find(e => e.symbol === selectedQuestion.targetElement);
        const correct =
            JSON.stringify(userShell) === JSON.stringify(element.shellConfig);
        setIsCorrect(correct);
        setFeedback(correct ? "✅ Correct!" : "❌ Incorrect. See the correct answer below.");
        setShowSolution(!correct);
        if (selectedQuestion && !answered.includes(selectedQuestion.id)) {
            setAnswered([...answered, selectedQuestion.id]);
        }
    }

    function handleNext() {
        let nextIndex = currentIndex + 1;
        while (nextIndex < questions.length && answered.includes(questions[nextIndex].id)) {
            nextIndex++;
        }
        if (nextIndex < questions.length) {
            handleSelectQuestion(questions[nextIndex]);
        } else {
            setStep("menu");
            setFeedback("");
            setShowSolution(false);
            setIsCorrect(null);
        }
    }

    <div className="practice-mode-bar" style={{
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 24
  }}>
    <button
      className={mode === "atom" ? "practice-mode-btn active" : "practice-mode-btn"}
      onClick={() => setMode("atom")}
    >
      🧪 Atom Practice
    </button>
    <button
      className={mode === "compound" ? "practice-mode-btn active" : "practice-mode-btn"}
      onClick={() => setMode("compound")}
    >
      🧬 Compound Practice
    </button>
  </div>

const na = elements.find(e => e.symbol === "Na");
const cl = elements.find(e => e.symbol === "Cl");

// Defensive loading for compound mode:
if (mode === "compound" && (!na || !cl)) {
  return (
    <>
      <div className="practice-mode-bar" style={{
        display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24
      }}>
        <button
          className={mode === "atom" ? "practice-mode-btn active" : "practice-mode-btn"}
          onClick={() => setMode("atom")}
        >
          🧪 Atom Practice
        </button>
        <button
          className={mode === "compound" ? "practice-mode-btn active" : "practice-mode-btn"}
          onClick={() => setMode("compound")}
        >
          🧬 Compound Practice
        </button>
      </div>
      <div style={{ padding: "2em", color: "#b44", textAlign: "center" }}>
        Loading atom data...
      </div>
    </>
  );
}
    // --- Layout: Side by Side ---
return (
  <>
    <div className="practice-mode-bar" style={{
      display: 'flex',
      gap: 12,
      justifyContent: 'center',
      marginBottom: 24
    }}>
      <button
        className={mode === "atom" ? "practice-mode-btn active" : "practice-mode-btn"}
        onClick={() => setMode("atom")}
      >
        🧪 Atom Practice
      </button>
      <button
        className={mode === "compound" ? "practice-mode-btn active" : "practice-mode-btn"}
        onClick={() => setMode("compound")}
      >
        🧬 Compound Practice
      </button>
    </div>

    {/* --- Atom Practice Section --- */}
    {mode === "atom" ? (
      <div className="advanced-chemistry-flex">
        {/* --- Your entire atom/bohr practice + periodic table code (copied from above) --- */}
        {/* Left: Practice Area */}
        <div className="advanced-chemistry-panel">
          {step === "menu" ? (
            <>
              <h2>Bohr Model & Atomic Structure Practice</h2>
              <div className="instructions" style={{ margin: "1em 0" }}>
                <ReactMarkdown>{instructions}</ReactMarkdown>
              </div>
              <h3>Choose a Practice Challenge:</h3>
              <select
                value={selectedQuestionId}
                onChange={handleDropdownChange}
                className="practice-dropdown"
              >
                <option value="">-- Select a challenge --</option>
                {questions.map((q, idx) => (
                  <option key={q.id} value={q.id}>
                    {`${idx + 1}. ${q.prompt.replace("${elementName}", q.targetElement)}`}
                  </option>
                ))}
              </select>
              <div className="progress-bar">
                {answered.length > 0 &&
                  `You have completed ${answered.length} out of ${questions.length} challenges.`}
              </div>
            </>
          ) : (
            (() => {
              const element = elements.find(e => e.symbol === selectedQuestion.targetElement);
              const showInput =
                selectedQuestion.type === "identify" ||
                selectedQuestion.type === "shell_count" ||
                selectedQuestion.type === "isotope" ||
                selectedQuestion.type === "ion";
              return (
                <>
                  <button onClick={() => setStep("menu")} className="back-btn">← Back to Menu</button>
                  <div className="progress-bar">
                    Challenge {currentIndex + 1} of {questions.length}
                  </div>
                  <h3>{selectedQuestion.prompt.replace("${elementName}", selectedQuestion.targetElement)}</h3>
                  {selectedQuestion.type === "build" ? (
                    <BohrModelBuilder
                      element={element}
                      onSubmit={handleBuildSubmit}
                      feedback={feedback}
                      isCorrect={isCorrect}
                      showSolution={showSolution}
                      solution={{ handleNext }}
                      explanation={selectedQuestion.explanation}
                    />
                  ) : (
                    <>
                      <div className="bohr-model-demo" style={{ margin: "1em 0", minHeight: 200 }}>
                        <BohrModelVisualizer element={element} />
                      </div>
                      {showInput && (
                        <div className="answer-area">
                          <input
                            type="text"
                            value={userAnswer}
                            onChange={e => setUserAnswer(e.target.value)}
                            placeholder="Enter your answer here"
                            className="answer-input"
                          />
                          <button onClick={handleSubmit} className="submit-btn">Submit</button>
                        </div>
                      )}
                      {feedback && (
                        <div className={`feedback ${isCorrect === false ? "incorrect" : isCorrect ? "correct" : ""}`}>
                          {feedback}
                        </div>
                      )}
                      {showSolution && (
                        <div className="solution-card">
                          <b>Correct Answer:</b> <span className="solution-answer">{selectedQuestion.answer}</span>
                          <br />
                          {selectedQuestion.explanation && (
                            <span className="explanation">{selectedQuestion.explanation}</span>
                          )}
                          <button onClick={handleNext} className="next-btn">Next Challenge</button>
                        </div>
                      )}
                      {isCorrect && (
                        <button onClick={handleNext} className="next-btn">Next Challenge</button>
                      )}
                    </>
                  )}
                </>
              );
            })()
          )}
        </div>
        {/* Right: Periodic Table */}
        <div className="advanced-chemistry-panel table-panel">
          <h2 style={{ marginBottom: "0.6em" }}>Periodic Table</h2>
          <PeriodicTable
            onElementSelect={handleElementSelect}
            selectedSymbol={selectedElementSymbol}
          />
          {selectedElementSymbol && (
            <div style={{ margin: "1.6em auto 0 auto", maxWidth: 340 }}>
              <BohrModelVisualizer
                element={elements.find(e => e.symbol === selectedElementSymbol)}
              />
            </div>
          )}
        </div>
      </div>
    ) : (
      /* --- Compound Practice Section --- */
      <div className="advanced-chemistry-panel" style={{ minHeight: 600, margin: "0 auto" }}>
        <CompoundPractice leftElement={na} rightElement={cl} />
      </div>
    )}
  </>
);
}
